import React, { useCallback, useEffect, useState } from 'react';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import Box from '@mui/material/Box';
import DateUtils from '../../../utils/dateUtils';
import TwCheckbox from '../../TwCheckboxes/TwCheckboxes';
import TwGenericMenu, { TwGenericMenuOptions } from '../../TwGenericMenu/TwGenericMenu';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import TwTooltip from '../../TwTooltip/TwTooltip';
import Stack from '@mui/material/Stack';
import NumberUtils from '../../../utils/numberUtils';
import './DetailFile.scss';
import { styled } from '@mui/material/styles';
import { ReactComponent as LockIcon } from './../../../assets/icons/lock.svg';
import { ReactComponent as PdfFileIcon } from './../../../assets/icons/pdf-file.svg';
import FileUtils from '../../../utils/fileUtils';
import TwBadge from '../../TwBadge/TwBadge';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';

export interface DetailFileProps {
  /**
   * File id
   */
  fileId?: string;
  /**
   * File name
   */
  fileName: string;
  /**
   * Classname
   */
  className?: string;
  /**
   * Type
   */
  type?: string;
  /**
   * Created at
   */
  created_at?: Date;
  /**
   * Can be selected: true or false
   */
  isSelectable?: boolean;
  /**
   * Action to perform on change
   */
  onInputChange?: (e: React.ChangeEvent<HTMLInputElement>) => void | undefined;
  /**
   * Default selected value
   */
  defaultSelected?: boolean;
  /**
   * Action to trigger when component is clicked
   */
  onClick?: () => void;
  /**
   * name for use with Formik
   */
  name?: string;
  /**
   * checked for use with Formik
   */
  checked?: boolean;
  /**
   * onEdit handler
   */
  onEdit?: () => void;
  /**
   * onDelete handler
   */
  onDelete?: () => void;
  /**
   * onDownload handler
   */
  onDownload?: () => void;
  /**
   * showMenu boolean for showing or hiding menu
   */
  showMenu?: boolean;
  /**
   * File size
   */
  size?: number;
  /**
   * visibility flag
   */
  driverVisibility?: boolean;
  /**
   * Set visibility to false
   */
  onVisibilityMenu?: (visibility: boolean) => void;
  /**
   * show visibility icon
   */
  showVisibility?: boolean;
  /**
   * Key prop for map renders
   */
  key?: string | number;
  /**
   * Is document encrypted
   */
  isEncrypted?: boolean;
  /**
   * disables checkbox for encrypted documents
   */
  disableEncryptedDocuments?: boolean;
  /**
   * uploadedBy
   */
  uploadedBy?: string;
  /**
   * File type
   */
  fileType?: string;
  fileKey?: string;
  tag?: string;
  attachmentId?: string;
  s3SignedUrl?: string;
  enableDelete?: boolean;
}

const StyledDetailFileContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isSelectable'
})<{ isSelectable: boolean | undefined }>(({ isSelectable }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  width: '100%',
  border: '1px solid #E2E2EA',
  borderRadius: '4px',
  height: '56px',
  padding: '8px',
  backgroundColor: isSelectable ? 'white' : 'none',
  fontFamily: 'Poppins',
  fontSize: '14px',
  lineHeight: '21px',
  marginBottom: '8px',
  color: '#7E858E'
}));

const DetailFile: React.FC<DetailFileProps> = ({
  fileId,
  fileName,
  className,
  type,
  created_at,
  isSelectable,
  defaultSelected,
  onInputChange,
  onClick,
  name,
  checked,
  onEdit,
  onDelete,
  onDownload,
  showMenu,
  driverVisibility,
  size,
  onVisibilityMenu,
  showVisibility = false,
  key,
  isEncrypted,
  disableEncryptedDocuments,
  uploadedBy,
  fileType,
  fileKey,
  tag,
  attachmentId,
  s3SignedUrl,
  enableDelete = false
}): JSX.Element => {
  const [src, setSrc] = useState<string>('');

  const options: TwGenericMenuOptions[] = [
    {
      label: driverVisibility ? 'Set to hidden from driver' : 'Set to visible to driver',
      action: (): void => {
        onVisibilityMenu && onVisibilityMenu(!driverVisibility);
      }
    },
    {
      label: 'Download',
      action: (): void => {
        onDownload && onDownload();
      }
    },
    {
      label: 'Edit',
      action: (): void => {
        onEdit && onEdit();
      }
    },
    {
      label: 'Delete',
      className: 'detail-file__delete',
      action: (): void => {
        onDelete && onDelete();
      }
    }
  ];

  const daysAgo = (created_at && DateUtils.getNumberOfDaysUntilNow(created_at)) || 0;

  const getDaysAgo = (): string => {
    if (daysAgo < 1) {
      return 'Today';
    }
    return `${daysAgo} ${daysAgo > 1 ? 'days ago' : 'day ago'}`;
  };

  const getUploadedBy = (): string => {
    return uploadedBy ? ` by ${uploadedBy}` : '';
  };

  const getFileInfoSection = (): JSX.Element => {
    return (
      <Box className="detail-file__mobileFileInfo">
        <Box className="detail-file__file" onClick={onClick}>
          <InsertDriveFileOutlinedIcon className="detail-file__adornment" />
          <Stack className="detail-file__filenameContainer">
            <Stack flexDirection="row" gap={1}>
              <span className="detail-file__filename">{fileName}</span>
            </Stack>
            <Stack flexDirection="row" gap={1}>
              <span className="detail-file__text">{type}</span>
              {created_at && (
                <span className="detail-file__text">
                  {getDaysAgo()}
                  {getUploadedBy()}
                </span>
              )}
            </Stack>
          </Stack>
        </Box>
      </Box>
    );
  };

  const getSrc = useCallback(async () => {
    if (s3SignedUrl) {
      setSrc(s3SignedUrl);

      return;
    }

    if (attachmentId) {
      FileUtils.getImageSrc(attachmentId)
        .then((src) => {
          setSrc(src);
        })
        .catch((error) => {
          console.error('Error getting image src: ', error);
        });
    }
  }, [attachmentId]);

  useEffect(() => {
    if (fileType && fileType.includes('image/') && attachmentId) {
      getSrc();
    }
  }, [fileType, getSrc]);

  return (
    <StyledDetailFileContainer isSelectable={isSelectable} className={className} key={fileKey}>
      {isSelectable && onInputChange && fileId && (
        <TwTooltip
          title={isEncrypted && disableEncryptedDocuments ? 'Encrypted files cannot be merged' : ''}
          placement="top"
        >
          <TwCheckbox
            id={fileId}
            name={name ?? fileName}
            value={fileId}
            label=""
            defaultChecked={defaultSelected}
            onInputChange={onInputChange}
            checked={checked}
            disabled={isEncrypted && disableEncryptedDocuments}
          />
        </TwTooltip>
      )}

      <Box className="detail-file__file" onClick={onClick}>
        {!isSelectable && !onInputChange && (
          <>
            {fileType && fileType.includes('image/') ? (
              <img src={src} width={40} height={40} />
            ) : (
              <PdfFileIcon />
            )}
          </>
        )}

        <Stack className="detail-file__filenameContainer">
          <Stack flexDirection="row" gap={1}>
            <span className="detail-file__filename">{fileName}</span>
            {tag && (
              <Chip
                sx={{
                  minWidth: '70px',
                  height: '23px',
                  fontSize: '10px',
                  fontWeight: '500',
                  '& .MuiChip-label': {
                    padding: '0'
                  }
                }}
                label={tag}
              />
            )}
            {isEncrypted && <LockIcon className="detail-file__lock" id="td-locked" />}
          </Stack>
          <Stack flexDirection="row" gap={1}>
            <span className="detail-file__text">{type}</span>
            {created_at && (
              <span className="detail-file__text">
                {getDaysAgo()}
                {getUploadedBy()}
              </span>
            )}
          </Stack>
        </Stack>
      </Box>
      <Stack flexDirection="row">
        {!driverVisibility && showVisibility && (
          <TwTooltip title={'Hidden from driver'} placement="top">
            <VisibilityOffIcon sx={{ color: '#9CA3AF', fontSize: '16px' }} />
          </TwTooltip>
        )}

        {showMenu && (
          <TwGenericMenu
            className="detail-file__menu"
            options={options}
            menuOwnerSection={getFileInfoSection()}
          />
        )}
        {enableDelete && !showMenu && (
          <IconButton
            className="detail-file__delete"
            onClick={(e): void => {
              e.stopPropagation();
              onDelete && onDelete();
            }}
          >
            <CloseIcon />
          </IconButton>
        )}
      </Stack>
    </StyledDetailFileContainer>
  );
};

DetailFile.defaultProps = {
  isSelectable: false
};

export default DetailFile;
